import React, { useEffect, useMemo, useCallback, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import Autocomplete from 'accessible-autocomplete/react';
import ErrorMessage from '../ErrorMessage';
import type {
  ErrorMessages,
  Rules,
  SelectOption,
  SelectOptionDefault,
  SelectOptionGroup,
} from '../../../config/types';
import { getInputRules, validateField } from '../../../utils/form-utils';
import { textareaMaxLength } from '../../../constants';
import Details from '../Details';
import { getSelectedConditionalQuestions } from '../../../utils/config-utils';
import Fieldset from '../Fieldset';
import TextInputField from '../TextInputField';
import Textarea from '../Textarea';
import SelectInputField from '../SelectInputField';

type Props = {
  id: string;
  items?: string[];
  options?: SelectOption[];
  name: string;
  title: string;
  hintText?: string;
  label?: string;
  errorMessages?: ErrorMessages;
  rules?: Rules;
  detailsHeading?: string;
  detailsContent?: string;
  matchLimit?: number;
  fuzzyMatch?: boolean;
  placeholder?: string;
  onValueConfirmed?: (value: string) => void;
  asyncSearch?: (query: string) => Promise<string[]>;
  isLoading?: boolean;
  loadingMessage?: string;
  minimumLoadingMessageLength?: number;
  suppressErrorMessage?: boolean;
  showRequiredFlag?: boolean;
};

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const normalizeAutocompleteValue = (value: string): string =>
  value.trim().toLowerCase().replace(/\s+/g, ' ');

const splitAutocompleteTokens = (value: string): string[] =>
  normalizeAutocompleteValue(value)
    .split(/[^a-z0-9]+/i)
    .map((token) => token.trim())
    .filter(Boolean);

const levenshteinDistance = (left: string, right: string): number => {
  if (left === right) return 0;
  if (left.length === 0) return right.length;
  if (right.length === 0) return left.length;

  const rows = left.length + 1;
  const cols = right.length + 1;
  const matrix = Array.from({ length: rows }, () => Array<number>(cols).fill(0));

  for (let i = 0; i < rows; i += 1) matrix[i][0] = i;
  for (let j = 0; j < cols; j += 1) matrix[0][j] = j;

  for (let i = 1; i < rows; i += 1) {
    for (let j = 1; j < cols; j += 1) {
      const cost = left[i - 1] === right[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost,
      );
    }
  }

  return matrix[left.length][right.length];
};

const getFuzzyMatchScore = (query: string, candidate: string): number => {
  const normalizedQuery = normalizeAutocompleteValue(query);
  const normalizedCandidate = normalizeAutocompleteValue(candidate);

  if (!normalizedQuery || !normalizedCandidate) return Number.POSITIVE_INFINITY;
  if (normalizedCandidate === normalizedQuery) return 0;

  const candidateTokens = [normalizedCandidate, ...splitAutocompleteTokens(normalizedCandidate)];
  const queryTokens = splitAutocompleteTokens(normalizedQuery);
  const searchTokens = queryTokens.length > 0 ? queryTokens : [normalizedQuery];

  let bestScore = Number.POSITIVE_INFINITY;

  for (const queryToken of searchTokens) {
    const distanceThreshold =
      queryToken.length <= 3 ? 1 : queryToken.length <= 6 ? 2 : 3;

    for (const candidateToken of candidateTokens) {
      if (!candidateToken) continue;

      const isExact = candidateToken === queryToken;
      const startsWithQuery = candidateToken.startsWith(queryToken);
      const containsQuery = candidateToken.includes(queryToken);
      const distance = levenshteinDistance(queryToken, candidateToken);
      const isCloseTypoMatch = distance <= distanceThreshold;

      if (!isExact && !startsWithQuery && !containsQuery && !isCloseTypoMatch) {
        continue;
      }

      const matchBucket = isExact ? 0 : startsWithQuery ? 1 : containsQuery ? 2 : 3;
      const score =
        matchBucket * 1000 +
        distance * 10 +
        Math.abs(candidateToken.length - queryToken.length);

      if (score < bestScore) {
        bestScore = score;
      }
    }
  }

  return bestScore;
};

export const getAutocompleteMatches = (
  query: string,
  sourceItems: string[],
  fuzzyMatch = false,
  matchLimit?: number,
): string[] => {
  const normalizedQuery = normalizeAutocompleteValue(query);
  const uniqueValues = Array.from(
    new Set(
      sourceItems
        .map((value) => String(value || '').trim())
        .filter((value) => value.length > 0),
    ),
  );

  if (!normalizedQuery) {
    const ordered = uniqueValues.sort((a, b) =>
      a.localeCompare(b, undefined, {
        sensitivity: 'base',
      }),
    );
    return typeof matchLimit === 'number' ? ordered.slice(0, matchLimit) : ordered;
  }

  if (!fuzzyMatch) {
    const filtered = uniqueValues.filter(
      (result) => normalizeAutocompleteValue(result).indexOf(normalizedQuery) !== -1,
    );
    return typeof matchLimit === 'number' ? filtered.slice(0, matchLimit) : filtered;
  }

  const ranked = uniqueValues
    .map((value) => ({
      value,
      score: getFuzzyMatchScore(normalizedQuery, value),
    }))
    .filter((result) => Number.isFinite(result.score))
    .sort((left, right) => {
      if (left.score !== right.score) return left.score - right.score;
      return left.value.localeCompare(right.value, undefined, { sensitivity: 'base' });
    })
    .map((result) => result.value);

  return typeof matchLimit === 'number' ? ranked.slice(0, matchLimit) : ranked;
};

function AutocompleteWrapper(props: Props) {
  const {
    id,
    items,
    options,
    hintText,
    label,
    name,
    errorMessages,
    title,
    rules,
    detailsHeading,
    detailsContent,
    matchLimit,
    fuzzyMatch = false,
    placeholder,
    onValueConfirmed,
    asyncSearch,
    isLoading = false,
    loadingMessage,
    minimumLoadingMessageLength = 1,
    suppressErrorMessage = false,
    showRequiredFlag,
  } = props;
  const { register, getValues, setValue, getFieldState, trigger, watch } = useFormContext();
  const selected = watch(name);
  const [isValuesPopupOpen, setIsValuesPopupOpen] = useState(false);
  const latestSearchRequestId = useRef(0);

  const inputRules = useMemo(
    () => ({ ...getInputRules(rules ?? {}, title, textareaMaxLength) }),
    [rules, title],
  );

  const sourceItems = useMemo(() => {
    if (Array.isArray(items) && items.length > 0) return items;
    if (!Array.isArray(options)) return [];

    const resolved: string[] = [];
    options.forEach((option) => {
      if (typeof option === 'string') {
        resolved.push(option);
      } else if ('label' in option && 'options' in option) {
        resolved.push(...(option as SelectOptionGroup).options);
      } else if ('option' in option) {
        resolved.push((option as SelectOptionDefault).option);
      }
    });
    return resolved;
  }, [items, options]);

  const sortedAvailableValues = useMemo(() => {
    const uniqueValues = Array.from(
      new Set(
        sourceItems
          .map((value) => String(value || '').trim())
          .filter((value) => value.length > 0),
      ),
    );
    return uniqueValues.sort((a, b) =>
      a.localeCompare(b, undefined, {
        sensitivity: 'base',
      }),
    );
  }, [sourceItems]);

  const popupBaseId = useMemo(() => {
    const raw = `${id || name || 'autocomplete'}-available-values`;
    return raw.replace(/[^a-zA-Z0-9_-]/g, '-');
  }, [id, name]);

  const popupTitleId = `${popupBaseId}-title`;
  const showAvailableValuesPopup = !asyncSearch;
  const currentValue = useMemo(() => {
    const watchedValue = typeof selected === 'string' ? selected : '';
    if (watchedValue.length > 0 || selected === '') return watchedValue;
    const formValue = getValues(name);
    return typeof formValue === 'string' ? formValue : '';
  }, [getValues, name, selected]);

  useEffect(() => {
    if (getValues(name) === undefined) {
      setValue(name, '');
    }
  }, [setValue]);

  const suggest = useCallback(
    (query: string, populateResults: (results: string[]) => void) => {
      if (asyncSearch) {
        const trimmedQuery = query.trim();
        const requestId = latestSearchRequestId.current + 1;
        latestSearchRequestId.current = requestId;

        if (!trimmedQuery) {
          populateResults([]);
          return;
        }

        const shouldShowLoadingMessage =
          isNonEmptyString(loadingMessage) && trimmedQuery.length >= minimumLoadingMessageLength;

        if (shouldShowLoadingMessage) {
          populateResults([loadingMessage]);
        } else {
          populateResults([]);
        }

        void asyncSearch(query)
          .then((results) => {
            if (latestSearchRequestId.current !== requestId) return;
            populateResults(getAutocompleteMatches(query, results, fuzzyMatch, matchLimit));
          })
          .catch(() => {
            if (latestSearchRequestId.current !== requestId) return;
            populateResults([]);
          });
        return;
      }

      populateResults(getAutocompleteMatches(query, sourceItems, fuzzyMatch, matchLimit));
    },
    [asyncSearch, fuzzyMatch, loadingMessage, matchLimit, minimumLoadingMessageLength, sourceItems],
  );

  const hooksFormRegister = register(name, {
    validate: (value: string) => validateField(title, inputRules, value, errorMessages),
  });

  const { error } = getFieldState(name);
  const conditionalQuestions = getSelectedConditionalQuestions(
    { name, title, questionType: 'autoComplete', options } as any,
    selected,
  );

  const handleConfirm = (value: string | undefined) => {
    if (value && value !== loadingMessage) {
      setValue(name, value, { shouldValidate: true });
      trigger(name);
      onValueConfirmed?.(value);
    }
  };

  const handleSelectFromPopup = (value: string) => {
    setValue(name, value, { shouldValidate: true, shouldDirty: true });
    trigger(name);
    onValueConfirmed?.(value);
    setIsValuesPopupOpen(false);
  };

  useEffect(() => {
    if (!isValuesPopupOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsValuesPopupOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isValuesPopupOpen]);

  return (
    <>
      <style>{`
        @keyframes autocomplete-wrapper-spin {
          to { transform: rotate(360deg); }
        }

        .autocomplete-input-shell {
          width: 100%;
        }

        .autocomplete-input-shell .autocomplete__wrapper {
          display: block;
          width: 100%;
        }

        .autocomplete-input-shell .autocomplete__input,
        .autocomplete-input-shell .autocomplete__hint,
        .autocomplete-input-shell .autocomplete__menu {
          box-sizing: border-box;
          width: 100%;
        }

        .autocomplete-field-row {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          width: 100%;
        }

        .autocomplete-field-row__input {
          flex: 1 1 auto;
          min-width: 0;
        }

        .autocomplete-field-row__suffix {
          width: 20px;
          min-width: 20px;
          height: 2.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .autocomplete-input-shell__spinner {
          width: 20px;
          height: 20px;
          border: 2px solid #b1b4b6;
          border-top-color: #1d70b8;
          border-radius: 50%;
          display: inline-block;
          animation: autocomplete-wrapper-spin 0.8s linear infinite;
          pointer-events: none;
          z-index: 2;
        }

        .autocomplete__menu .autocomplete__option:has(.autocomplete__status-option) {
          pointer-events: none;
          color: #505a5f;
        }

        .autocomplete__status-option {
          display: block;
        }

        .autocomplete-values-popup__trigger {
          background: none;
          border: 0;
          padding-top: 0;
          padding-right: 0;
          padding-bottom: 0;
          padding-left: 25px;
          text-align: left;
        }

        .autocomplete-values-popup__trigger:hover {
          cursor: pointer;
        }
      `}</style>
      {hintText ? (
        <>
          <label className="govuk-fieldset__legend govuk-fieldset__legend--s" htmlFor={name}>
            {label}
          </label>
          <div className="govuk-hint">{hintText}</div>
        </>
      ) : (
        <label className="govuk-label" htmlFor={name}>
          {label}
        </label>
      )}

      {error && !suppressErrorMessage && <ErrorMessage error={error} />}
      {detailsHeading && <Details heading={detailsHeading} content={detailsContent} />}

      {showAvailableValuesPopup && (
        <button
          type="button"
          className="autocomplete-values-popup__trigger govuk-details__summary"
          aria-haspopup="dialog"
          aria-expanded={isValuesPopupOpen}
          aria-controls={popupBaseId}
          onClick={() => setIsValuesPopupOpen(true)}
        >
          <span className="govuk-details__summary-text">Show available options</span>
        </button>
      )}

      <div className="govuk-!-width-three-quarters">
        <div className="autocomplete-field-row">
          <div className="autocomplete-field-row__input">
            <div className="autocomplete-input-shell" data-testid="autocomplete-input-shell">
              <Autocomplete
                key={`${popupBaseId}-${currentValue}`}
                {...hooksFormRegister}
                defaultValue={currentValue}
                id={id}
                name={name}
                source={suggest}
                confirmOnBlur
                onConfirm={handleConfirm}
                placeholder={placeholder}
                templates={{
                  suggestion: (value: string) =>
                    value === loadingMessage ? (
                      `<span class="autocomplete__status-option">${value}</span>`
                    ) : (
                      value
                    ),
                }}
              />
            </div>
          </div>
          <div className="autocomplete-field-row__suffix" aria-hidden={!isLoading}>
            {isLoading && (
              <span
                className="autocomplete-input-shell__spinner"
                aria-live="polite"
                aria-label="Searching"
              />
            )}
          </div>
        </div>
      </div>

      {showAvailableValuesPopup && isValuesPopupOpen && (
        <div
          className="autocomplete-values-popup-overlay"
          role="presentation"
          onClick={() => setIsValuesPopupOpen(false)}
        >
          <div
            id={popupBaseId}
            role="dialog"
            aria-modal="true"
            aria-labelledby={popupTitleId}
            className="autocomplete-values-popup"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="autocomplete-values-popup__header">
              <h2 id={popupTitleId} className="govuk-heading-m govuk-!-margin-bottom-0">
                Available values
              </h2>
              <button
                type="button"
                className="govuk-link autocomplete-values-popup__close"
                onClick={() => setIsValuesPopupOpen(false)}
              >
                Close
              </button>
            </div>
            <div className="autocomplete-values-popup__content">
              {sortedAvailableValues.length === 0 ? (
                <p className="govuk-body govuk-!-margin-bottom-0">No values available.</p>
              ) : (
                <ul className="govuk-list govuk-list--bullet govuk-!-margin-bottom-0">
                  {sortedAvailableValues.map((value) => (
                    <li key={value}>
                      <button
                        type="button"
                        className="autocomplete-values-popup__item-button govuk-link"
                        onClick={() => handleSelectFromPopup(value)}
                      >
                        {value}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {conditionalQuestions.map((cq) => {
        const childName = cq.name;
        const defaultChildValue = getValues(childName) ?? '';

        return (
          <div
            key={childName}
            className="govuk-radios__conditional govuk-radios__conditional--visible govuk-!-margin-top-4"
          >
            <Fieldset
              title={cq.title}
              hintText={cq.hint}
              legendId={`${childName}-legend`}
              showRequiredAsterisk={showRequiredFlag === true && cq.rules?.required === true}
            >
              <>
                {cq.questionType === 'textInput' && (
                  <TextInputField
                    title={cq.title}
                    name={childName}
                    approver={cq.approver}
                    allowSelfApprover={cq.allowSelfApprover}
                    restrictDomains={cq.restrictDomains}
                    placeholder={cq.placeholder}
                    wordCount={cq.wordCount}
                    characterCount={cq.characterCount}
                    rules={cq.rules}
                    defaultValue={defaultChildValue}
                    errorMessages={cq.errorMessages}
                    detailsHeading={cq.detailsHeading}
                    detailsContent={cq.detailsContent}
                  />
                )}

                {cq.questionType === 'textarea' && (
                  <Textarea
                    title={cq.title}
                    name={childName}
                    placeholder={cq.placeholder}
                    wordCount={cq.wordCount}
                    characterCount={cq.characterCount}
                    textRows={cq.textRows}
                    rules={cq.rules}
                    defaultValue={defaultChildValue}
                    errorMessages={cq.errorMessages}
                    detailsHeading={cq.detailsHeading}
                    detailsContent={cq.detailsContent}
                  />
                )}

                {cq.questionType === 'select' && (
                  <SelectInputField
                    name={childName}
                    id={`${childName}-select`}
                    hintOption={cq.hintOption}
                    options={cq.options || []}
                    rules={cq.rules}
                    title={cq.title}
                    errorMessages={cq.errorMessages}
                    detailsHeading={cq.detailsHeading}
                    detailsContent={cq.detailsContent}
                    defaultValue={typeof cq.defaultValue === 'string' ? cq.defaultValue : undefined}
                    showRequiredFlag={showRequiredFlag}
                  />
                )}
              </>
            </Fieldset>
          </div>
        );
      })}
    </>
  );
}
AutocompleteWrapper.displayName = 'Autocomplete';

export default AutocompleteWrapper;
